import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Button from './Button';

// Desktop: nur die fünf wichtigsten Ziele – der Rest bleibt als Scroll-Anker
// über das Mobile-Menü und die Seite selbst erreichbar
const primaryNav = [
  { id: 'rundflug', label: 'Rundflüge' },
  { id: 'ueber-uns', label: 'Über uns' },
  { id: 'pilot-werden', label: 'Pilot werden' },
  { id: 'flugplatz', label: 'Flugplatz' },
  { id: 'kontakt', label: 'Kontakt' },
];

const fullNav = [
  { id: 'rundflug', label: 'Rundflüge' },
  { id: 'ueber-uns', label: 'Über uns' },
  { id: 'impressionen', label: 'Impressionen' },
  { id: 'pilot-werden', label: 'Pilot werden' },
  { id: 'ausbildung', label: 'Ausbildung' },
  { id: 'flugzeuge', label: 'Flugzeuge' },
  { id: 'flugplatz', label: 'Flugplatz' },
  { id: 'veranstaltungen', label: 'Veranstaltungen' },
  { id: 'kontakt', label: 'Kontakt' },
];

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Scroll-Funktion für die Startseite – der Abstand zum Header kommt
  // aus `scroll-margin-top` in index.css
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    // Wenn wir nicht auf der Startseite sind, navigiere zur Startseite mit Anker
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }

    // Mobile Menu schließen
    setIsMenuOpen(false);

    // Kurz warten, bis das eingeklappte Menü das Layout freigegeben hat
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Scroll-to-top Funktion für das Logo
  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Wenn wir nicht auf der Startseite sind, navigiere zur Startseite
    if (location.pathname !== '/') {
      window.location.href = '/';
      return;
    }
    
    // Wenn wir auf der Startseite sind, scrolle nach oben
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Club Name - jetzt als Link zur Startseite */}
          <div className="flex items-center">
            <NavLink to="/" className="flex-shrink-0 flex items-center hover:opacity-80 transition-opacity" onClick={scrollToTop}>
              <img 
                src="/images/logo.png" 
                alt="Flugsportverein Otto Lilienthal Bad Langensalza" 
                className="h-12 w-auto"
              />
              <div className="ml-3 flex flex-col">
                <h1 className="text-sm font-bold text-gray-900 leading-tight">
                  Flugsportverein
                </h1>
                <h2 className="text-sm font-semibold text-gray-900 leading-tight">
                  Otto Lilienthal
                </h2>
              </div>
            </NavLink>
          </div>

          {/* Desktop Navigation: 5 Hauptziele + hervorgehobener Buchungs-CTA */}
          <div className="hidden md:flex md:items-center">
            <nav className="flex space-x-1 items-center mr-4">
              {primaryNav.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => scrollToSection(e, item.id)}
                  className="text-gray-900 hover:text-primary px-3 py-2 text-sm font-medium whitespace-nowrap rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <Button
              variant="accent"
              size="sm"
              href="#rundflug"
              onClick={(e) => scrollToSection(e as React.MouseEvent<HTMLAnchorElement>, 'rundflug')}
            >
              Rundflug buchen
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Menü öffnen</span>
              {/* Icon when menu is closed */}
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Icon when menu is open */}
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, toggle classes based on menu state */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {fullNav.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => scrollToSection(e, item.id)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:text-primary hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {item.label}
            </a>
          ))}
          <div className="pt-2 px-3 pb-1">
            <Button
              variant="accent"
              href="#rundflug"
              onClick={(e) => scrollToSection(e as React.MouseEvent<HTMLAnchorElement>, 'rundflug')}
              className="w-full"
            >
              Rundflug buchen
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 